import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePlanContext, planSdkKeys } from '@onxite/plan-sdk';

/**
 * Post-Stripe landing page. Stripe redirects here with
 * `?session_id={CHECKOUT_SESSION_ID}` after a successful checkout. We poll
 * `/plans/checkout/confirm/:sessionId` for up to 30s waiting for the webhook
 * to materialise the EcInvoicePlan row, then invalidate the current-plan
 * cache so every consumer of the SDK picks up the new caps.
 */
export function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { api } = usePlanContext();
  const startedAt = useRef(Date.now());
  const [status, setStatus] = useState<'pending' | 'ready' | 'timeout'>(
    'pending',
  );

  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) {
      navigate('/billing', { replace: true });
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.confirmCheckout(sessionId);
        if (cancelled) return;
        if (res.status === 'ready') {
          await queryClient.invalidateQueries({
            queryKey: planSdkKeys.current(),
          });
          setStatus('ready');
          setTimeout(() => navigate('/billing', { replace: true }), 1200);
          return;
        }
        if (Date.now() - startedAt.current > 30_000) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled && Date.now() - startedAt.current < 30_000) {
          setTimeout(poll, 2000);
        } else {
          setStatus('timeout');
        }
      }
    };
    void poll();

    return () => {
      cancelled = true;
    };
  }, [params, navigate, api, queryClient]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6">
      {status === 'pending' && (
        <>
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--color-border-default)] border-t-[var(--color-accent)]" />
          <p className="text-sm text-[var(--color-fg-muted)]">
            Confirming your payment…
          </p>
        </>
      )}
      {status === 'ready' && (
        <>
          <h2 className="text-2xl font-bold">Payment received</h2>
          <p className="text-sm text-[var(--color-fg-muted)]">
            Your new plan is active. Redirecting…
          </p>
        </>
      )}
      {status === 'timeout' && (
        <>
          <h2 className="text-2xl font-bold">Still processing</h2>
          <p className="max-w-md text-center text-sm text-[var(--color-fg-muted)]">
            Stripe is taking longer than usual to confirm. Your invoice will
            appear on the billing page shortly.
          </p>
          <button
            onClick={() => navigate('/billing', { replace: true })}
            className="rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-semibold text-[var(--color-fg-on-accent)]"
          >
            Go to billing
          </button>
        </>
      )}
    </div>
  );
}
